import type { Map } from "maplibre-gl";
import { MAP_LAYER_IDS } from "../../config/map.config";

type ImageCoordinates = [[number, number], [number, number], [number, number], [number, number]];

export class ImageOverlayService {
  private readonly sourceId = "image-overlay-source";
  private imageUrl: string | null = null;
  private coordinates: ImageCoordinates | null = null;
  private opacity = 0.65;

  constructor(private readonly map: Map) {}

  load(url: string): void {
    this.imageUrl = url;
    this.coordinates = this.coordinatesFromCurrentBounds();
    this.refresh();
  }

  setOpacity(opacity: number): void {
    this.opacity = Math.max(0, Math.min(1, opacity));
    if (this.map.getLayer(MAP_LAYER_IDS.imageOverlay)) {
      this.map.setPaintProperty(MAP_LAYER_IDS.imageOverlay, "raster-opacity", this.opacity);
    }
  }

  scale(factor: number): void {
    if (!this.coordinates) return;
    const center = this.center();
    this.coordinates = this.coordinates.map(([lng, lat]) => [center.lng + (lng - center.lng) * factor, center.lat + (lat - center.lat) * factor]) as ImageCoordinates;
    this.refresh();
  }

  move(direction: "up" | "down" | "left" | "right", ratio = 0.08): void {
    if (!this.coordinates) return;
    const width = Math.abs(this.coordinates[1][0] - this.coordinates[0][0]);
    const height = Math.abs(this.coordinates[0][1] - this.coordinates[2][1]);
    const dx = direction === "left" ? -width * ratio : direction === "right" ? width * ratio : 0;
    const dy = direction === "down" ? -height * ratio : direction === "up" ? height * ratio : 0;
    this.coordinates = this.coordinates.map(([lng, lat]) => [lng + dx, lat + dy]) as ImageCoordinates;
    this.refresh();
  }

  rotate(degrees: number): void {
    if (!this.coordinates) return;
    const center = this.center();
    const radians = (degrees * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    this.coordinates = this.coordinates.map(([lng, lat]) => {
      const dx = lng - center.lng;
      const dy = lat - center.lat;
      return [center.lng + dx * cos - dy * sin, center.lat + dx * sin + dy * cos];
    }) as ImageCoordinates;
    this.refresh();
  }

  remove(): void {
    if (this.map.getLayer(MAP_LAYER_IDS.imageOverlay)) this.map.removeLayer(MAP_LAYER_IDS.imageOverlay);
    if (this.map.getSource(this.sourceId)) this.map.removeSource(this.sourceId);
    this.imageUrl = null;
    this.coordinates = null;
  }

  private refresh(): void {
    if (!this.imageUrl || !this.coordinates) return;
    if (this.map.getLayer(MAP_LAYER_IDS.imageOverlay)) this.map.removeLayer(MAP_LAYER_IDS.imageOverlay);
    if (this.map.getSource(this.sourceId)) this.map.removeSource(this.sourceId);

    this.map.addSource(this.sourceId, {
      type: "image",
      url: this.imageUrl,
      coordinates: this.coordinates
    });

    this.map.addLayer({
      id: MAP_LAYER_IDS.imageOverlay,
      type: "raster",
      source: this.sourceId,
      paint: { "raster-opacity": this.opacity }
    });
  }

  private coordinatesFromCurrentBounds(): ImageCoordinates {
    const bounds = this.map.getBounds();
    const west = bounds.getWest();
    const east = bounds.getEast();
    const north = bounds.getNorth();
    const south = bounds.getSouth();
    return [
      [west, north],
      [east, north],
      [east, south],
      [west, south]
    ];
  }

  private center(): { lng: number; lat: number } {
    if (!this.coordinates) return { lng: 0, lat: 0 };
    const lng = this.coordinates.reduce((sum, point) => sum + point[0], 0) / 4;
    const lat = this.coordinates.reduce((sum, point) => sum + point[1], 0) / 4;
    return { lng, lat };
  }
}
